import { z } from 'zod';

export const UnsubscribeMethodSchema = z.enum([
  'rfc8058_post',
  'mailto',
  'preference_web',
]);
export type UnsubscribeMethod = z.infer<typeof UnsubscribeMethodSchema>;

export const MailingListStatusSchema = z.enum([
  'identified',
  'request_submitted',
  'external_opened',
  'outcome_unknown',
  'failed',
  'kept',
]);
export type MailingListStatus = z.infer<typeof MailingListStatusSchema>;

export const UserPreferenceSchema = z.enum([
  'none',
  'keep_subscribed',
  'dismissed',
]);
export type UserPreference = z.infer<typeof UserPreferenceSchema>;

export const MailingListEmailItemSchema = z.object({
  id: z.string(),
  messageId: z.string(),
  subject: z.string(),
  snippet: z.string().nullable(),
  date: z.string(),
  isUnread: z.boolean(),
  isStarred: z.boolean(),
  isImportant: z.boolean(),
  hasHttpsUnsubscribeUrl: z.boolean().optional(),
  unsubscribeMailto: z.object({
    recipient: z.string(),
    subject: z.string().nullable(),
    body: z.string().nullable(),
  }).nullable().optional(),
  labels: z.array(z.string()),
});
export type MailingListEmailItem = z.infer<typeof MailingListEmailItemSchema>;

export const MailingListSummarySchema = z.object({
  id: z.string().uuid(),
  listKey: z.string(),
  name: z.string(),
  senderEmail: z.string(),
  domain: z.string(),
  isRecommended: z.boolean(),
  vetoReasons: z.array(z.string()),
  method: UnsubscribeMethodSchema,
  destinationHost: z.string(),
  hasOneClick: z.boolean(),
  totalObservedCount: z.number().int().nonnegative(),
  agedUnreadCount: z.number().int().nonnegative(),
  agedTotalCount: z.number().int().nonnegative(),
  agedUnreadRate: z.number().min(0).max(1),
  firstObservedAt: z.string().nullable(),
  lastObservedAt: z.string().nullable(),
  latestReceivedReadAt: z.string().nullable(),
  labelCheckedAt: z.string().nullable(),
  status: MailingListStatusSchema,
  userPreference: UserPreferenceSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MailingListSummary = z.infer<typeof MailingListSummarySchema>;

export const ListMailingListsQuerySchema = z.object({
  scope: z.enum(['recommended', 'all']).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListMailingListsQuery = z.infer<typeof ListMailingListsQuerySchema>;

export const ListMailingListsResponseSchema = z.object({
  lists: z.array(MailingListSummarySchema),
  nextCursor: z.string().nullable(),
  recommendedCount: z.number().int().nonnegative(),
  allCount: z.number().int().nonnegative(),
  dailyCapRemaining: z.number().int().nonnegative(),
  dailyCapTotal: z.number().int().positive(),
  isPro: z.boolean(),
  requestsAvailable: z.boolean(),
});
export type ListMailingListsResponse = z.infer<typeof ListMailingListsResponseSchema>;

export const DiscoverMailingListsRequestSchema = z.object({
  cursor: z.string().min(1).max(2048).optional(),
}).strict();
export const DiscoverMailingListsResponseSchema = z.object({
  examinedCount: z.number().int().min(0).max(100),
  discoveredCount: z.number().int().min(0).max(10),
  nextCursor: z.string().nullable(),
});

export const GetMailingListEmailsResponseSchema = z.object({
  listId: z.string().uuid(),
  emails: z.array(MailingListEmailItemSchema),
  observedCount: z.number().int().nonnegative(),
  returnedCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  nextCursor: z.string().nullable(),
  complete: z.boolean(),
  asOf: z.string(),
  evidenceWindowDays: z.number().int().positive().nullable(),
});
export type GetMailingListEmailsResponse = z.infer<typeof GetMailingListEmailsResponseSchema>;

export const PrepareMailingListUrlRequestSchema = z.object({
  referenceId: z.uuid(),
}).strict();
export const PrepareMailingListUrlResponseSchema = z.object({
  host: z.string().min(1).max(253).regex(/^[a-z0-9.-]+$/),
  capabilityVersion: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const PrepareMailingListRequestResponseSchema = PrepareMailingListUrlResponseSchema.extend({
  oneClickSupported: z.boolean(),
});
export const RevealMailingListUrlRequestSchema = PrepareMailingListUrlRequestSchema.extend({
  capabilityVersion: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
}).strict();
export const RevealMailingListUrlResponseSchema = z.object({
  host: z.string().min(1).max(253).regex(/^[a-z0-9.-]+$/),
  url: z.string().max(8192).refine((value) => {
    try { return new URL(value).protocol === 'https:'; } catch { return false; }
  }),
});

export const SetMailingListPreferenceRequestSchema = z.object({
  preference: UserPreferenceSchema,
});
export type SetMailingListPreferenceRequest = z.infer<typeof SetMailingListPreferenceRequestSchema>;

export const SetMailingListPreferenceResponseSchema = z.object({
  listId: z.string().uuid(),
  userPreference: UserPreferenceSchema,
  updatedAt: z.string(),
});
export type SetMailingListPreferenceResponse = z.infer<typeof SetMailingListPreferenceResponseSchema>;

export const CreateMailingListReviewDraftRequestSchema = z.object({
  selectedListIds: z.array(z.string().uuid()).min(1).max(500),
  billingInterval: z.enum(['month', 'year']).optional().default('month'),
});
export type CreateMailingListReviewDraftRequest = z.infer<typeof CreateMailingListReviewDraftRequestSchema>;

export const MailingListReviewDraftSchema = z.object({
  id: z.string().uuid(),
  selectedListIds: z.array(z.string().uuid()),
  billingInterval: z.enum(['month', 'year']),
  expiresAt: z.string(),
  createdAt: z.string(),
});
export type MailingListReviewDraft = z.infer<typeof MailingListReviewDraftSchema>;

export const CreateMailingListReviewDraftResponseSchema = MailingListReviewDraftSchema;

export const GetLatestMailingListReviewDraftResponseSchema = z.object({
  draft: MailingListReviewDraftSchema.nullable(),
});
export type GetLatestMailingListReviewDraftResponse = z.infer<typeof GetLatestMailingListReviewDraftResponseSchema>;

export const CreateMailingListRequestsRequestSchema = z.object({
  confirmed: z.literal(true),
  consentRevision: z.literal('mailing-list-request-v1'),
  requests: z.array(z.object({
    listId: z.uuid(),
    referenceId: z.uuid(),
    capabilityVersion: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    idempotencyKey: z.uuid(),
  }).strict()).min(1).max(500),
}).strict().superRefine((request, context) => {
  if (new Set(request.requests.map((item) => item.listId)).size !== request.requests.length
    || new Set(request.requests.map((item) => item.idempotencyKey)).size !== request.requests.length) {
    context.addIssue({ code: 'custom', message: 'Each list and request key must be unique', path: ['requests'] });
  }
});
export type CreateMailingListRequestsRequest = z.infer<typeof CreateMailingListRequestsRequestSchema>;

export const MailingListRequestResultSchema = z.discriminatedUnion('status', [
  z.object({
    listId: z.uuid(), operationId: z.uuid(),
    status: z.enum(['queued', 'claimed', 'sending', 'request_submitted', 'failed_no_send', 'outcome_unknown', 'cancelled']),
    destinationHost: z.string().min(1), url: z.string().nullable().optional(), error: z.null(),
  }),
  z.object({
    listId: z.uuid(), operationId: z.null(), status: z.literal('rejected'), destinationHost: z.null(),
    url: z.null().optional(),
    error: z.enum(['not_available', 'conflict', 'temporarily_unavailable']),
  }),
]);
export type MailingListRequestResult = z.infer<typeof MailingListRequestResultSchema>;

export const CompleteMailingListRequestRequestSchema = z.object({
  status: z.enum(['request_submitted', 'failed_no_send']),
}).strict();
export type CompleteMailingListRequestRequest = z.infer<typeof CompleteMailingListRequestRequestSchema>;

export const CompleteMailingListRequestResponseSchema = z.object({
  id: z.uuid(),
  status: z.enum(['request_submitted', 'failed_no_send']),
  finishedAt: z.string(),
});
export type CompleteMailingListRequestResponse = z.infer<typeof CompleteMailingListRequestResponseSchema>;

export const CreateMailingListRequestsResponseSchema = z.object({
  results: z.array(MailingListRequestResultSchema),
});
export type CreateMailingListRequestsResponse = z.infer<typeof CreateMailingListRequestsResponseSchema>;

export const MailingListOperationItemSchema = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid().nullable(),
  listKey: z.string(),
  method: UnsubscribeMethodSchema,
  destinationHost: z.string(),
  status: MailingListStatusSchema,
  httpStatus: z.number().int().nullable(),
  errorMessage: z.string().nullable(),
  submittedAt: z.string(),
});
export type MailingListOperationItem = z.infer<typeof MailingListOperationItemSchema>;

export const MailingListDispatchItemSchema = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid().nullable(),
  listKey: z.string(),
  name: z.string().nullable(),
  destinationHost: z.string(),
  status: z.enum(['queued', 'claimed', 'sending', 'request_submitted', 'failed_no_send', 'outcome_unknown', 'cancelled']),
  httpStatus: z.number().int().nullable(),
  approvedAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type MailingListDispatchItem = z.infer<typeof MailingListDispatchItemSchema>;

export const ListMailingListOperationsResponseSchema = z.object({
  requests: z.array(MailingListDispatchItemSchema),
});
export type ListMailingListOperationsResponse = z.infer<typeof ListMailingListOperationsResponseSchema>;
